import { IonContent } from '@ionic/react';
import React from 'react';
import { useFormContext } from 'react-hook-form';

import { amplitudeModule } from '@/modules/amplitude';
import {
  OnboardingOptionItem,
  OnboardingOptionsList,
} from '@/pages/Onboarding/components/List';
import { OnboardingNavigation } from '@/pages/Onboarding/components/Navigation';
import { useOnboarding } from '@/pages/Onboarding/context';
import { OwnAiTitle } from '@/pages/Onboarding/Layouts/OwnAI/components/Title';
import { OnbOptionType } from '@/pages/Onboarding/types';
import { nextStepTimeout } from '@/pages/Onboarding/utils/nextStepTimeout';
import { userService } from '@/services/user';
import { canLogEvents } from '@/utils/canLogEvents';

import { getOrientationCategories } from '../../utils/getOrientationCategories';

import {
  IDENTIFIES_ROMANTICALLY_OPTIONS_FEMALE,
  IDENTIFIES_ROMANTICALLY_OPTIONS_MALE,
} from './constants';
import { IdentifiesRomanticallyStyles } from './IdentifiesRomantically.styles';

import type { LayoutDataType } from '../../types';

export const ScreenIdentifiesRomantically: React.FC = () => {
  React.useEffect(() => {
    if (canLogEvents()) {
      amplitudeModule.logEvent('onboarding_screen', {
        seen: 'IdentifiesRomantically',
      });
    }
  }, []);

  const { wizard } = useOnboarding();
  const form = useFormContext<LayoutDataType>();
  const gender = form.watch('gender');
  const identifiesRomantically = form.watch('identifies_romantically');

  const title =
    gender === 'female'
      ? 'How she identifies romantically?'
      : 'How he identifies romantically?';

  const options =
    gender === 'female'
      ? IDENTIFIES_ROMANTICALLY_OPTIONS_FEMALE
      : IDENTIFIES_ROMANTICALLY_OPTIONS_MALE;

  const handleChange = (
    option: OnbOptionType<LayoutDataType['identifies_romantically']>,
  ) => {
    form.setValue('identifies_romantically', option.id);
    const mapping = getOrientationCategories(gender, option.id);
    userService.createOrUpdate({
      ...(mapping && {
        categories: mapping.categories,
      }),
      onboarding_data: {
        identifies_romantically: option.id,
        ...(mapping && { first_categories: mapping.first_categories }),
      },
    });
    nextStepTimeout(wizard.nextStep);
  };

  return (
    <IdentifiesRomanticallyStyles>
      <OnboardingNavigation />

      <IonContent>
        <OwnAiTitle>{title}</OwnAiTitle>
        <OnboardingOptionsList>
          {options.map((option) => (
            <OnboardingOptionItem
              checked={identifiesRomantically === option.id}
              control={form.control}
              option={option}
              name="identifies_romantically"
              key={option.id}
              onChange={() => handleChange(option)}
              TEST_ID={`identifies_romantically-${option.id}`}
            />
          ))}
        </OnboardingOptionsList>
      </IonContent>
    </IdentifiesRomanticallyStyles>
  );
};
